<template>
  <div class="payment-status-page">
    <AppNavbar />

    <main class="container status-content">
      <!-- LOADING -->
      <p v-if="isLoading" class="status-text centered">
        Checking transaction status...
      </p>

      <!-- ERROR -->
      <p v-else-if="errorMessage" class="status-text error centered">
        {{ errorMessage }}
      </p>

      <template v-else>
        <!-- PAGE HEAD -->
        <div class="page-head-row">
          <div class="page-head">
            <p class="page-title">Order confirmed</p>
            <p v-if="orderId" class="page-sub">Order #{{ orderId }}</p>
          </div>

          <button
            v-if="showCv"
            type="button"
            class="allocate-btn"
            @click="openAllocateCvPopup"
          >
            <i class="ri-flow-chart"></i> Allocate CV
          </button>
        </div>

        <!-- STATUS BANNER -->
        <div class="status-banner" :class="bannerClass">
          <div class="status-icon" :class="bannerClass">
            <i :class="bannerIcon"></i>
          </div>
          <div class="status-info">
            <p class="status-label">{{ bannerLabel }}</p>
            <p class="status-msg">{{ bannerMessage }}</p>
          </div>
          <div
            style="
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              gap: 2px;
            "
          >
            <span class="amount-chip">{{
              formatCurrency(orderData.orderAmount)
            }}</span>
            <span style="font-size: 10px">(Incl. of taxes)</span>
          </div>
        </div>

        <!-- TRANSACTION SUMMARY -->
        <div v-if="summaryRows.length" class="card">
          <p class="card-label">Transaction summary</p>
          <div class="rows">
            <div v-for="row in summaryRows" :key="row.label" class="row">
              <span class="rl">{{ row.label }}</span>
              <span class="rv" :class="row.cls">{{ row.value }}</span>
            </div>
          </div>
        </div>

        <div v-if="hdfcVerifyRows.length" class="card">
          <p class="card-label">Payment verification</p>
          <div class="rows">
            <div v-for="row in hdfcVerifyRows" :key="row.label" class="row">
              <span class="rl">{{ row.label }}</span>
              <span class="rv" :class="row.cls || ''">{{ row.value }}</span>
            </div>
          </div>
        </div>

        <!-- ORDER ITEMS -->
        <div v-if="orderItems.length" class="card">
          <p class="card-label">Order items</p>
          <div class="items-stack">
            <div
              v-for="item in orderItems"
              :key="item.itemSkuCode || item.itemCode || item.id"
              class="item-card"
            >
              <div class="item-thumb">
                <i class="ri-shopping-bag-line"></i>
              </div>
              <div class="item-info">
                <p class="item-name">{{ formatValue(item.itemName) }}</p>
                <p class="item-sub">
                  SKU: {{ formatValue(item.itemSkuCode) }} &nbsp;·&nbsp; Qty:
                  {{ formatValue(item.itemQuantity) }}
                  <span v-if="item.itemMRP">
                    &nbsp;·&nbsp; MRP: {{ formatCurrency(item.itemMRP) }}</span
                  >
                </p>
              </div>
              <span class="item-amt">{{ formatCurrency(item.itemPrice) }}</span>
            </div>
          </div>
        </div>

        <!-- PAYMENT TRANSACTIONS -->
        <div v-if="paymentTransactions.length" class="card">
          <p class="card-label">Payment transaction</p>
          <div
            v-for="tx in paymentTransactions"
            :key="tx.referenceId || tx.transactionReferenceId || tx.id"
            class="tx-card"
          >
            <div class="tx-top">
              <div class="tx-icon"><i class="ri-bank-card-line"></i></div>
              <span class="tx-name">{{
                formatValue(tx.paymentType || tx.type)
              }}</span>
              <span class="tx-status" :class="txStatusClass(tx.status)">
                {{ formatValue(tx.status) }}
              </span>
            </div>
            <div class="tx-rows">
              <div class="tx-row">
                <span class="tx-rl">Amount charged</span>
                <span class="tx-rv">{{
                  formatCurrency(tx.amountApplied)
                }}</span>
              </div>
              <div v-if="tx.referenceId" class="tx-row">
                <span class="tx-rl">Reference ID</span>
                <span class="tx-rv mono">{{ tx.referenceId }}</span>
              </div>
              <div v-if="tx.transactionReferenceId" class="tx-row">
                <span class="tx-rl">Transaction ref</span>
                <span class="tx-rv mono">{{ tx.transactionReferenceId }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- BACK HOME -->

        <button type="button" class="home-btn" @click="goHome">
          <i class="ri-home-4-line"></i> Back to home
        </button>
      </template>
    </main>

    <div
      v-if="showAllocateCvPopup"
      class="cv-popup-overlay"
      @click.self="showAllocateCvPopup = false"
    >
      <div class="cv-popup-box">
        <button
          type="button"
          class="cv-popup-close"
          @click="showAllocateCvPopup = false"
        >
          <i class="ri-close-line"></i>
          Close
        </button>
        <iframe
          class="cv-popup-iframe"
          :src="allocateCvUrl"
          title="Allocate CV"
        ></iframe>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as ecomSdk from "rdep-ecom-sdk";
import { useCartStore } from "../../stores/useCartStore.js";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const checkTransactionStatus = ecomSdk.checkTransactionStatus;
const verifyPaymentStatus = ecomSdk.verifyStatus || null;
const ENABLE_ORDER_STATUS_API = false;

const route = useRoute();
const router = useRouter();
const cartStore = useCartStore();

const isLoading = ref(false);
const errorMessage = ref("");
const transactionResponse = ref(null);
const showAllocateCvPopup = ref(false);
const allocateCvUrl = ref("");
const PLACE_ORDER_RESPONSE_STORAGE_KEY = "place.order.api.response";

const BA_URL = String(process.env.VUE_APP_BA_URL || "https://ba-ui.qa.rdep.io");
const HDFC_CONTEXT_STORAGE_KEY = "hdfc.payment.context";
const hdfcVerifyResponse = ref(null);

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const showCv = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const userType = normalizeRoleToken(
      user?.userType || user?.role || user?.loginRole || user?.customerType,
    );
    return userType === "DISTRIBUTOR";
  } catch (error) {
    return false;
  }
});

function getSavedHdfcContext() {
  try {
    const raw = window.localStorage.getItem(HDFC_CONTEXT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
}

function sanitizeUid(value) {
  const wrappers = new Set(['"', "'", "`", "(", ")", "[", "]"]);
  let next = String(value || "").trim();

  while (next.length && wrappers.has(next.charAt(0))) {
    next = next.slice(1).trim();
  }

  while (next.length && wrappers.has(next.charAt(next.length - 1))) {
    next = next.slice(0, -1).trim();
  }

  return next;
}

function extractUidFromUrlLike(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const cleaned = raw
    .replace(/^[\s"'`(]+/, "")
    .replace(/[\s"'`)]+$/, "")
    .trim();

  const candidates = [cleaned];
  try {
    candidates.push(decodeURIComponent(cleaned));
  } catch (error) {
    // ignore decode failures
  }

  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      const uid = sanitizeUid(
        parsed.searchParams.get("uid") ||
          parsed.searchParams.get("UID") ||
          parsed.searchParams.get("paymentUid") ||
          parsed.searchParams.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    } catch (error) {
      const qIndex = candidate.indexOf("?");
      if (qIndex < 0) {
        continue;
      }

      const params = new URLSearchParams(candidate.slice(qIndex + 1));
      const uid = sanitizeUid(
        params.get("uid") ||
          params.get("UID") ||
          params.get("paymentUid") ||
          params.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    }
  }

  return "";
}

function extractUidFromCurrentLocation() {
  if (typeof window === "undefined") return "";

  const href = String(window.location.href || "").trim();
  if (!href) return "";

  const direct = extractUidFromUrlLike(href);
  if (direct) return direct;

  const decodedCandidates = [href];
  try {
    decodedCandidates.push(decodeURIComponent(href));
  } catch (error) {
    // ignore decode failures
  }

  for (const candidate of decodedCandidates) {
    const match = candidate.match(
      /(?:[?&#]|^)\s*(uid|UID|paymentUid|paymentUID)=([^&#]+)/,
    );
    if (match && match[2]) {
      return sanitizeUid(match[2]);
    }
  }

  return "";
}

const orderId = computed(() => {
  const fromQuery = String(route.query?.orderId || "").trim();
  if (fromQuery) return fromQuery;
  return String(getSavedHdfcContext()?.orderId || "").trim();
});

const hdfcUid = computed(() => {
  const fromPathParam = sanitizeUid(route.params?.uid || "");
  if (fromPathParam) return fromPathParam;

  const fromQuery = sanitizeUid(route.query?.uid || "");
  if (fromQuery) return fromQuery;

  const fromReturnUrl = extractUidFromUrlLike(
    route.query?.returnUrl || route.query?.returnURL || "",
  );
  if (fromReturnUrl) return fromReturnUrl;

  const fromCurrentLocation = extractUidFromCurrentLocation();
  if (fromCurrentLocation) return fromCurrentLocation;

  const fromSavedReturnUrl = extractUidFromUrlLike(
    getSavedHdfcContext()?.returnUrl || getSavedHdfcContext()?.returnURL || "",
  );
  if (fromSavedReturnUrl) return fromSavedReturnUrl;

  return sanitizeUid(getSavedHdfcContext()?.uid || "");
});

const rootPayload = computed(() => {
  const data =
    transactionResponse.value?.data || transactionResponse.value || {};
  return data?.data || data;
});

const orderData = computed(() => rootPayload.value?.order || {});

const orderItems = computed(() =>
  Array.isArray(orderData.value?.orderItems) ? orderData.value.orderItems : [],
);

const paymentTransactions = computed(() =>
  Array.isArray(orderData.value?.paymentTransactions)
    ? orderData.value.paymentTransactions
    : [],
);

// ── FORMAT HELPERS ──────────────────────────────────────────
function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

// ── PAYMENT SUCCESS CHECK ───────────────────────────────────
function isPaymentSuccessful(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const statusCode = String(data.statusCode || nested.statusCode || "").trim();
  const statusText = String(
    data.status ||
      data.transactionStatus ||
      data.paymentStatus ||
      nested.status ||
      nested.transactionStatus ||
      nested.paymentStatus ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    statusCode === "200" &&
    ["success", "paid", "completed"].includes(statusText)
  )
    return true;
  return ["success", "paid", "completed"].includes(statusText);
}

// ── BANNER ──────────────────────────────────────────────────
const bannerClass = computed(() =>
  isPaymentSuccessful(transactionResponse.value) ? "success" : "pending",
);
const bannerIcon = computed(() =>
  isPaymentSuccessful(transactionResponse.value)
    ? "ri-check-line"
    : "ri-time-line",
);
const bannerLabel = computed(() =>
  isPaymentSuccessful(transactionResponse.value)
    ? "Payment successful"
    : "Payment pending",
);
const bannerMessage = computed(() =>
  isPaymentSuccessful(transactionResponse.value)
    ? "Your order is confirmed and being prepared."
    : "We're still confirming your payment. Check back shortly.",
);

// ── SUMMARY ROWS ────────────────────────────────────────────
const summaryRows = computed(() => {
  const payload = rootPayload.value || {};
  const order = orderData.value || {};
  const statusText = String(
    order.orderStatus || payload.status || "",
  ).toLowerCase();
  const isPaid = ["success", "paid", "completed"].includes(statusText);

  return [
    {
      label: "Order status",
      value: formatValue(order.orderStatus || payload.status),
      cls: isPaid ? "tag-success" : "tag-pending",
    },
    // {
    //   label: "Payment method",
    //   value: formatValue(order.paidBy || order.billType),
    //   cls: "",
    // },
    { label: "Bill ID", value: formatValue(order.billId), cls: "mono" },
    {
      label: "Transaction date",
      value: formatValue(order.transactionDate || order.createdDate),
      cls: "",
    },
    // { label: "Store", value: formatValue(order.storeCode), cls: "" },
  ].filter((row) => row.value !== "-");
});

const hdfcVerifyRows = computed(() => {
  const payload =
    hdfcVerifyResponse.value?.data || hdfcVerifyResponse.value || {};
  const nested = payload?.data || {};
  const amount =
    payload?.amount ??
    payload?.billAmount ??
    nested?.amount ??
    nested?.billAmount ??
    null;
  const referenceId = String(
    payload?.referenceId ||
      nested?.referenceId ||
      payload?.transactionId ||
      nested?.transactionId ||
      "",
  ).trim();

  return [
    { label: "Reference ID", value: formatValue(referenceId), cls: "mono" },
    // { label: "Status code", value: formatValue(statusCode) },
    // {
    //   label: "Status",
    //   value: formatValue(status),
    //   cls: txStatusClass(status),
    // },
    // { label: "Message", value: formatValue(statusMessage) },
    {
      label: "Amount",
      value:
        amount !== null && amount !== undefined ? formatCurrency(amount) : "-",
    },
  ].filter((row) => row.value !== "-");
});

// ── TX STATUS PILL ──────────────────────────────────────────
function txStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["success", "paid", "completed"].includes(s)) return "tag-success";
  if (["failed", "failure"].includes(s)) return "tag-failed";
  return "tag-pending";
}

// ── FETCH ───────────────────────────────────────────────────
async function fetchTransactionStatus() {
  // Hidden intentionally: retained for reference, disabled by default.
  if (!ENABLE_ORDER_STATUS_API) {
    return;
  }

  if (typeof checkTransactionStatus !== "function") {
    return;
  }

  if (!orderId.value) {
    return;
  }

  try {
    const response = await checkTransactionStatus(orderId.value);
    if (!transactionResponse.value) {
      transactionResponse.value = response || null;
    }
  } catch (error) {
    // no-op
  }
}

async function fetchHdfcVerification() {
  isLoading.value = true;
  errorMessage.value = "";

  if (!hdfcUid.value) {
    transactionResponse.value = null;
    hdfcVerifyResponse.value = null;
    errorMessage.value = "Payment UID is missing in the URL.";
    isLoading.value = false;
    return;
  }

  if (typeof verifyPaymentStatus !== "function") {
    transactionResponse.value = null;
    hdfcVerifyResponse.value = null;
    errorMessage.value = "verifyStatus API is not available in the SDK.";
    isLoading.value = false;
    return;
  }

  try {
    const response = await verifyPaymentStatus(hdfcUid.value);
    transactionResponse.value = response || null;
    hdfcVerifyResponse.value = response || null;
    if (isPaymentSuccessful(response)) {
      await cartStore.clearCartAfterPayment();
    }
  } catch (error) {
    transactionResponse.value = null;
    hdfcVerifyResponse.value = null;
    const message =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to verify payment status.";

    errorMessage.value = message;
  } finally {
    isLoading.value = false;
  }
}

function goHome() {
  window.localStorage.removeItem(HDFC_CONTEXT_STORAGE_KEY);
  router.push({ name: "home" });
}

function getSavedPlaceOrderBillId() {
  try {
    const raw = window.localStorage.getItem(PLACE_ORDER_RESPONSE_STORAGE_KEY);
    if (!raw) return "";
    const response = JSON.parse(raw);
    const data = response?.data || response || {};
    const nested = data?.data || {};
    const order = data?.order || nested?.order || {};
    return String(
      data?.billId || nested?.billId || order?.billId || response?.billId || "",
    ).trim();
  } catch (error) {
    return "";
  }
}

function openAllocateCvPopup() {
  const registerRaw = window.localStorage.getItem("register.api.response");
  const authRaw = window.localStorage.getItem("auth.currentUser");
  const registerPayload = registerRaw ? JSON.parse(registerRaw) : {};
  const authPayload = authRaw ? JSON.parse(authRaw) : {};
  const registerData = registerPayload?.data || registerPayload || {};
  const authUser = authPayload?.data || authPayload || {};

  const baId = String(
    registerData?.distributorCode ||
      authUser?.distributorCode ||
      authUser?.distributorCode,
  ).trim();
  const baName = String(
    registerData?.firstName ||
      registerData?.name ||
      authUser?.firstName ||
      authUser?.name ||
      authUser?.fullName ||
      authUser?.customerName ||
      "",
  ).trim();
  const orderRef = String(
    getSavedPlaceOrderBillId() || orderData.value?.billId || "",
  ).trim();

  if (!baId || !orderRef) {
    errorMessage.value =
      "Unable to open Allocate CV. Missing BA ID or Order Reference.";
    return;
  }

  errorMessage.value = "";
  // ${BA_URL}/?step=5&baId=BA-1002&baName=Will%20Smith&orderRef=OR-1003&registrationToken=abc
  allocateCvUrl.value = `${BA_URL}/?step=5&baId=${encodeURIComponent(
    baId,
  )}&baName=${encodeURIComponent(baName)}&orderRef=${encodeURIComponent(
    orderRef,
  )}&registrationToken=abc`;
  // console.log("cv", allocateCvUrl.value);

  showAllocateCvPopup.value = true;
}

onMounted(async () => {
  await fetchTransactionStatus();
  await fetchHdfcVerification();
});
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.payment-status-page {
  min-height: 100vh;
  background: #f8fafc;
}
.container {
  width: 92%;
  max-width: 680px;
  margin: 0 auto;
}
.status-content {
  padding: 32px 0 64px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-head-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

/* PAGE HEAD */
.page-head {
  margin-bottom: 4px;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0a2240;
  margin: 0;
}
.page-sub {
  font-size: 13px;
  color: #5a6b82;
  margin: 4px 0 0;
}

/* STATUS BANNER */
.status-banner {
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.status-banner.success {
  background: #eaf3de;
  border: 1px solid #97c459;
}
.status-banner.pending {
  background: #faeeda;
  border: 1px solid #ef9f27;
}

.status-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.status-icon.success {
  background: #639922;
  color: #fff;
}
.status-icon.pending {
  background: #ba7517;
  color: #fff;
}

.status-info {
  flex: 1;
  min-width: 0;
}
.status-label {
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
  margin: 0;
}
.status-msg {
  font-size: 12px;
  color: #5a6b82;
  margin: 3px 0 0;
}

.amount-chip {
  font-size: 18px;
  font-weight: 800;
  color: #0a2240;
  white-space: nowrap;
  flex-shrink: 0;
}

/* CARD */
.card {
  background: #fff;
  border: 1px solid rgba(0, 61, 165, 0.09);
  border-radius: 14px;
  padding: 18px 20px;
}
.card-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: #003da5;
  text-transform: uppercase;
  margin-bottom: 14px;
}

/* SUMMARY ROWS */
.rows {
  display: flex;
  flex-direction: column;
}
.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #edf2f7;
}
.rows .row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.rows .row:first-child {
  padding-top: 0;
}
.rl {
  font-size: 13px;
  color: #5a6b82;
  font-weight: 600;
}
.rv {
  font-size: 13px;
  color: #0a2240;
  font-weight: 700;
  text-align: right;
  overflow-wrap: anywhere;
  max-width: 62%;
}
.rv.mono {
  font-family: "Courier New", Courier, monospace;
  font-size: 12px;
}
.rv.tag-success {
  background: #eaf3de;
  color: #3b6d11;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
}
.rv.tag-pending {
  background: #faeeda;
  color: #854f0b;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
}
.rv.tag-failed {
  background: #fce8e8;
  color: #991b1b;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
}

/* ITEMS */
.items-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.item-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fafcff;
}
.item-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #edf2f7;
  border: 1px solid #e2eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5a6b82;
  font-size: 20px;
  flex-shrink: 0;
}
.item-info {
  flex: 1;
  min-width: 0;
}
.item-name {
  font-size: 13px;
  font-weight: 700;
  color: #0a2240;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.item-sub {
  font-size: 12px;
  color: #5a6b82;
  margin: 3px 0 0;
}
.item-amt {
  font-size: 13px;
  font-weight: 800;
  color: #0a2240;
  flex-shrink: 0;
}

/* PAYMENT TX */
.tx-card {
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 14px;
  background: #fafcff;
}
.tx-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.tx-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(0, 61, 165, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #003da5;
  font-size: 16px;
  flex-shrink: 0;
}
.tx-name {
  font-size: 13px;
  font-weight: 700;
  color: #0a2240;
}
.tx-status {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
}
.tx-status.tag-success {
  background: #eaf3de;
  color: #3b6d11;
}
.tx-status.tag-pending {
  background: #faeeda;
  color: #854f0b;
}
.tx-status.tag-failed {
  background: #fce8e8;
  color: #991b1b;
}

.tx-rows {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.tx-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}
.tx-rl {
  color: #5a6b82;
  font-weight: 600;
}
.tx-rv {
  color: #0a2240;
  font-weight: 700;
}
.tx-rv.mono {
  font-family: "Courier New", Courier, monospace;
}

/* allocate-btn */
.allocate-btn {
  width: auto;
  min-width: 168px;
  height: 46px;
  border-radius: 999px;
  border: none;
  background: #f5b000;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  transition: all 0.2s;
}

.allocate-btn:hover {
  /* border-color: #003da5; */
  color: #f5b000;
  background: #003da5;
}
/* HOME BTN */
.home-btn {
  width: 100%;
  height: 46px;
  border-radius: 999px;
  border: none;
  background: #f5b000;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
  transition: all 0.2s;
}
.home-btn:hover {
  /* border-color: #003da5; */
  color: #f5b000;
  background: #003da5;
}

.cv-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cv-popup-box {
  width: min(1100px, 96vw);
  height: min(760px, 92vh);
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(10, 34, 64, 0.35);
  display: flex;
  flex-direction: column;
}

.cv-popup-close {
  height: 44px;
  border: none;
  border-bottom: 1px solid #e2eaf6;
  background: #fff;
  color: #0a2240;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  align-self: flex-end;
  font-family: inherit;
}

.cv-popup-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  flex: 1;
}

/* STATUS TEXT */
.status-text {
  font-size: 14px;
  color: #5a6b82;
  margin: 0;
}
.status-text.error {
  color: #b91c1c;
  font-weight: 700;
}
.status-text.centered {
  text-align: center;
  padding: 60px 0;
}

@media (max-width: 600px) {
  .status-content {
    padding: 22px 0 44px;
    gap: 12px;
  }
  .page-head-row {
    flex-direction: column;
    align-items: stretch;
  }
  .allocate-btn {
    width: 100%;
  }
  .status-banner {
    flex-wrap: wrap;
    padding: 14px;
    gap: 10px;
  }
  .amount-chip {
    font-size: 16px;
    margin-left: auto;
  }
  .card {
    padding: 14px;
  }
  .row {
    align-items: flex-start;
  }
  .rl,
  .rv {
    font-size: 12px;
  }
  .rv {
    max-width: 58%;
  }
  .tx-top {
    flex-wrap: wrap;
  }
  .tx-status {
    margin-left: 0;
  }
  .tx-row {
    gap: 8px;
    align-items: flex-start;
  }
  .tx-rv,
  .tx-rv.mono {
    text-align: right;
    overflow-wrap: anywhere;
  }
  .item-name {
    white-space: normal;
  }
}
</style>
